import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { cx } from './cx.js';

/**
 * Puce de filtre (`aria-pressed`). Sélectionnée : fond d'encre sombre, pour rester neutre quelle que
 * soit la marque ; la marque est réservée à l'action principale et à la navigation active.
 */
export function Chip({
  selected,
  count,
  children,
  className,
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & { selected: boolean; count?: number; children: ReactNode }) {
  return (
    <button
      type="button"
      aria-pressed={selected}
      className={cx(
        'inline-flex min-h-(--ctl-xs) shrink-0 items-center gap-1.5 rounded-full px-3 text-sm font-medium whitespace-nowrap pointer-fine:px-2.5',
        'transition-[background-color,color,box-shadow] duration-150 ease-out',
        selected
          ? 'bg-ink text-surface'
          : 'bg-surface text-ink-2 ring-1 ring-inset ring-line hover:bg-hover hover:text-ink hover:ring-line-strong',
        className,
      )}
      {...rest}
    >
      {children}
      {count !== undefined ? (
        <span className={cx('tabular-nums', selected ? 'text-surface/70' : 'text-ink-3')}>{count}</span>
      ) : null}
    </button>
  );
}

/**
 * Barre de filtres : une rangée de puces qui défile horizontalement sur téléphone (bord à bord) et
 * passe à la ligne sur ordinateur. `trailing` : contenu à droite (tri, recherche…).
 */
export function FilterBar({
  label,
  children,
  trailing,
  className,
}: {
  label: string;
  children: ReactNode;
  trailing?: ReactNode;
  className?: string;
}) {
  return (
    <div className={cx('flex items-center gap-3', className)}>
      <div
        role="group"
        aria-label={label}
        className="scroll-row -mx-[var(--page-x)] flex min-w-0 flex-1 gap-1.5 overflow-x-auto px-[var(--page-x)] py-0.5 lg:mx-0 lg:flex-wrap lg:overflow-visible lg:px-0"
      >
        {children}
      </div>
      {trailing}
    </div>
  );
}

/** Barre d'outils simple : éléments alignés, espacés, qui passent à la ligne. */
export function Toolbar({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cx('flex flex-wrap items-center gap-2', className)}>{children}</div>;
}
